import { ISelectedItem } from '../../../components/Inputs/Selects/MultiSelectCheck';
import { ExtendedRight, IOptions } from '../../../interfaces/Rights';
import { INDICATOR_TYPE_STRUCTURAL } from '../../../utils/const';
import type { AppliedGroup } from '../components/SearchAndFilters/types';
import { FilterSelection, resolveFilterTree, selectionFromIds } from './filterTree';

// Selección completa del filtro: la del modal (borrador) o la aplicada (URL)
export interface FilterDraft extends FilterSelection {
    rights: string[];
    ods: ISelectedItem[];
    priorityGroups: ISelectedItem[];
}

// ids de cada filtro, con el nombre de su param en la URL
export interface FilterIds {
    rightId: string[];
    type: string[];
    dimensionID: string[];
    subdimensionID: string[];
    indicatorID: string[];
    odsID: string[];
    priorityGroups: string[];
}

// de dónde salen las opciones para pasar de ids a seleccionados (y al revés)
export interface DraftSources {
    rights: ExtendedRight[];
    odsOptions: IOptions[];
    priorityGroupsOptions: IOptions[];
}

export const EMPTY_DRAFT: FilterDraft = {
    rights: [],
    types: [],
    dimensions: [],
    subdimensions: [],
    indicators: [],
    ods: [],
    priorityGroups: [],
};

export const toLabels = (items: ISelectedItem[]) => items.map(item => item.label);

export const countDraft = (draft: FilterDraft) =>
    Object.values(draft).reduce((total, values) => total + values.length, 0);

// clave de comparación de una lista, sin importar el orden
const toKey = (values: ReadonlyArray<string | ISelectedItem>) =>
    values.map(v => typeof v === "string" ? v : v.value).sort().join("|");

export const isSameDraft = (a: FilterDraft, b: FilterDraft) =>
    (Object.keys(a) as (keyof FilterDraft)[]).every(key => toKey(a[key]) === toKey(b[key]));

// con solo "Estructural" elegido no aplican los ODS
export const isOnlyStructural = (types: string[]) =>
    types.length === 1 && types[0] === INDICATOR_TYPE_STRUCTURAL;

const byIds = (options: IOptions[], ids: string[]) =>
    options.filter(o => ids.includes(String(o.id)));

// ids aplicados (URL) -> borrador
export const draftFromIds = (ids: FilterIds, { rights, odsOptions, priorityGroupsOptions }: DraftSources): FilterDraft => {
    const selectedRights = rights.filter(r => ids.rightId.includes(String(r.id)));

    // se buscan dentro de los derechos y tipos elegidos (unificados por nombre)
    const { dimensions, subdimensions, indicators } = selectionFromIds(selectedRights, ids.type, {
        dimensions: ids.dimensionID,
        subdimensions: ids.subdimensionID,
        indicators: ids.indicatorID,
    });

    return {
        rights: selectedRights.map(r => r.titulo),
        types: ids.type,
        dimensions,
        subdimensions,
        indicators,
        ods: byIds(odsOptions, ids.odsID),
        priorityGroups: byIds(priorityGroupsOptions, ids.priorityGroups),
    };
};

// cascada del árbol: sin derechos no hay tipos/dimensiones/...; y se descarta lo que ya no cuelga de lo elegido
export const resolveDraft = (draft: FilterDraft, rights: ExtendedRight[]): FilterDraft => {
    const selection: FilterSelection = draft.rights.length
        ? draft
        : { types: [], dimensions: [], subdimensions: [], indicators: [] };

    const draftRights = rights.filter(r => draft.rights.includes(r.titulo));
    const { kept } = resolveFilterTree(draftRights, selection);

    return { ...draft, types: selection.types, ...kept };
};

// quita un valor (por su label) de un campo del borrador y aplica la cascada
export const removeFromDraft = (draft: FilterDraft, key: keyof FilterDraft, label: string, rights: ExtendedRight[]): FilterDraft => {
    const values: (string | ISelectedItem)[] = draft[key];
    const newDraft: FilterDraft = {
        ...draft,
        [key]: values.filter(v => (typeof v === "string" ? v : v.label) !== label),
    };
    if (isOnlyStructural(newDraft.types)) newDraft.ods = [];
    return resolveDraft(newDraft, rights);
};

// borrador -> pills de "Filtros aplicados" (key = campo del borrador)
export const toAppliedGroups = (draft: FilterDraft): AppliedGroup[] => [
    { key: "rights", values: draft.rights },
    { key: "types", values: draft.types },
    { key: "dimensions", values: toLabels(draft.dimensions) },
    { key: "subdimensions", values: toLabels(draft.subdimensions) },
    { key: "indicators", values: toLabels(draft.indicators) },
    { key: "ods", values: toLabels(draft.ods) },
    { key: "priorityGroups", values: toLabels(draft.priorityGroups) },
];
